import React, { useState, useEffect, useMemo } from 'react';
import { pickupTaskService } from '../../services/pickupTaskService';
import { employeeService } from '../../services/employeeService';
import { shipmentService } from '../../services/shipmentService';
import { catalogService } from '../../services/catalogService';
import { containerService } from '../../services/containerService';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { X, ZoomIn, Download, FileDown } from 'lucide-react';
import { realtimeService } from '../../services/realtimeService';
import CollectedClientsTable from './components/CollectedClientsTable';
import AssignToContainerModal from './components/AssignToContainerModal';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// ✅ Icône pour les marqueurs chauffeurs sur la carte (DivIcon, pas d'image externe)
const driverIcon = new L.DivIcon({
  html: `<div style="background-color:#16a34a; width:20px; height:20px; border-radius:50%; border:3px solid white; box-shadow:0 0 4px rgba(0,0,0,0.5);"></div>`,
  className: '',
  iconSize: [20, 20],
  iconAnchor: [10, 10]
});

// ✅ Caches pour éviter de refaire les mêmes appels API
const geocodeCache = {};
const etaCache = {};

// ✅ Convertit une adresse texte en coordonnées GPS (Nominatim, gratuit)
async function geocodeAddress(address) {
  if (!address) return null;
  if (geocodeCache?.[address]) return geocodeCache?.[address];
  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address)}`);
    const data = await res?.json();
    if (data?.[0]) {
      const coords = { lat: parseFloat(data?.[0]?.lat), lng: parseFloat(data?.[0]?.lon) };
      geocodeCache[address] = coords;
      return coords;
    }
  } catch (e) {
    console.error('Erreur géocodage:', e);
  }
  return null;
}

// ✅ Calcule le temps de trajet en minutes entre deux points (OSRM, gratuit)
async function getRouteDurationMinutes(originLat, originLng, destLat, destLng) {
  const key = `${originLat},${originLng}-${destLat},${destLng}`;
  if (etaCache?.[key] != null) return etaCache?.[key];
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${originLng},${originLat};${destLng},${destLat}?overview=false`;
    const res = await fetch(url);
    const data = await res?.json();
    const durationSec = data?.routes?.[0]?.duration;
    if (durationSec != null) {
      const minutes = Math.round(durationSec / 60);
      etaCache[key] = minutes;
      return minutes;
    }
  } catch (e) {
    console.error('Erreur calcul ETA:', e);
  }
  return null;
}

// ✅ Composant affichant l'ETA pour une tâche donnée
const TaskETA = ({ task, driverLocations, employees }) => {
  const [eta, setEta] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const compute = async () => {
      if (!task?.driverId || !task?.clientAddress || !task?.clientCity) {
        setEta(null);
        return;
      }

      const emp = employees?.find(e => e?.id === task?.driverId);
      const authUserId = emp?.authUserId || emp?.auth_user_id;
      if (!authUserId) {
        setEta(null);
        return;
      }

      const driverLoc = driverLocations?.find(l => l?.driver_id === authUserId);
      if (!driverLoc) {
        setEta(null);
        return;
      }

           setLoading(true);
      let dest = await geocodeAddress(`${task?.clientAddress}, ${task?.clientCity}, France`);
      let approximate = false;

      // ✅ NEW: Si l'adresse précise échoue, retente avec juste la ville
      if (!dest && task?.clientCity) {
        dest = await geocodeAddress(`${task?.clientCity}, France`);
        approximate = true;
      }

      if (!dest || cancelled) {
        setLoading(false);
        return;
      }

      const minutes = await getRouteDurationMinutes(
        driverLoc?.latitude,
        driverLoc?.longitude,
        dest?.lat,
        dest?.lng
      );

      if (!cancelled) {
        setEta(minutes != null ? { value: minutes, approximate } : null);
        setLoading(false);
      }
    };

    compute();

    return () => {
      cancelled = true;
    };
  }, [task?.id, task?.driverId, driverLocations, employees]);

  if (!task?.driverId) return <span className="text-xs text-gray-400">-</span>;
  if (loading) return <span className="text-xs text-gray-400">Calcul...</span>;
   if (eta == null) return <span className="text-xs text-gray-400">-</span>;
  return (
    <span className="text-xs font-medium text-green-700">
      ≈ {eta?.value} min{eta?.approximate ? ' (approx.)' : ''}
    </span>
  );
};

// ✅ Helper function to generate navigation URLs
const generateNavigationUrl = (type, address, postalCode, city) => {
  const query = `${address},${postalCode},${city},France`?.replace(/\s+/g, '+');
  
  if (type === 'google') {
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  } else if (type === 'waze') {
    return `https://waze.com/ul?q=${address},${postalCode},${city}&navigate=yes`;
  }
  return '';
};

// ✅ Helper function to check if pickup is in France
const isPickupInFrance = (task) => {
  if (task?.shipmentSenderCountry) {
    return task?.shipmentSenderCountry === 'France';
  }
  const frenchCityPattern = /\b75[0-9]{3}\b|\bparis\b/i;
  return frenchCityPattern?.test(task?.clientCity || '');
};

// ✅ Helper function to generate complete route URLs for multiple stops
const generateCompleteRouteUrl = (tasks, optimize = false) => {
  if (!tasks || tasks?.length === 0) return '';
  
  const franceTasks = tasks?.filter(task => 
    isPickupInFrance(task) && task?.clientAddress && task?.clientCity
  );
  
  if (franceTasks?.length === 0) return '';
  
  const waypoints = franceTasks?.map(task => {
    const postalCode = task?.clientCity?.match(/\b\d{5}\b/)?.[0] || '';
    return `${task?.clientAddress},${postalCode},${task?.clientCity},France`;
  })?.join('|');
  
  const lastTask = franceTasks?.[franceTasks?.length - 1];
  const lastPostalCode = lastTask?.clientCity?.match(/\b\d{5}\b/)?.[0] || '';
  const destination = `${lastTask?.clientAddress},${lastPostalCode},${lastTask?.clientCity},France`;
  
  const waypointsWithoutLast = franceTasks?.slice(0, -1)?.map(task => {
    const postalCode = task?.clientCity?.match(/\b\d{5}\b/)?.[0] || '';
    return `${task?.clientAddress},${postalCode},${task?.clientCity},France`;
  })?.join('|');
  
  const baseUrl = 'https://www.google.com/maps/dir/?api=1';
  const optimizeParam = optimize ? 'optimize:true|' : '';
  
  if (franceTasks?.length === 1) {
    return `https://www.google.com/maps/search/?api=1&query=${destination?.replace(/\s+/g, '+')}`;
  }
  
  return `${baseUrl}&destination=${destination?.replace(/\s+/g, '+')}&waypoints=${optimizeParam}${waypointsWithoutLast?.replace(/\s+/g, '+')}`;
};

// ✅ Helper function to check if route has France pickups
const hasAnyFrancePickups = (tasks) => {
  return tasks?.some(task => isPickupInFrance(task));
};

// ✅ Navigation buttons component
const NavigationButtons = ({ task }) => {
  if (!isPickupInFrance(task) || !task?.clientAddress || !task?.clientCity) {
    return null;
  }
  
  const postalCode = task?.clientCity?.match(/\b\d{5}\b/)?.[0] || '';
  
  return (
    <div className="flex gap-2 mt-2">
      <a
        href={generateNavigationUrl('google', task?.clientAddress, postalCode, task?.clientCity)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
        onClick={(e) => e?.stopPropagation()}
      >
        📍 Google Maps
      </a>
      <a
        href={generateNavigationUrl('waze', task?.clientAddress, postalCode, task?.clientCity)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition"
        onClick={(e) => e?.stopPropagation()}
      >
        🚗 Waze
      </a>
    </div>
  );
};

// ✅ Composant carte des positions chauffeurs (FIX: z-index isolé)
const DriverLocationsMap = ({ locations }) => {
  const center = locations?.length > 0
    ? [locations?.[0]?.latitude, locations?.[0]?.longitude]
    : [48.8566, 2.3522];

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Position des chauffeurs</h2>
      <div style={{ height: '400px', width: '100%', position: 'relative', zIndex: 0 }}>
        <MapContainer center={center} zoom={11} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap contributors'
          />
          {locations?.map((loc) => (
            <Marker key={loc?.driver_id} position={[loc?.latitude, loc?.longitude]} icon={driverIcon}>
              <Popup>
                Chauffeur
                <br />
                Mis à jour : {new Date(loc?.updated_at)?.toLocaleTimeString('fr-FR')}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

const PickupTaskPlanningSystem = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [containers, setContainers] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [selectedCatalogItems, setSelectedCatalogItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showRouteSheet, setShowRouteSheet] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [photoSignedUrl, setPhotoSignedUrl] = useState(null);
  const [photoError, setPhotoError] = useState('');
  const [photoSignedUrls, setPhotoSignedUrls] = useState([]);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showAssignContainerModal, setShowAssignContainerModal] = useState(false);
  const [taskToAssign, setTaskToAssign] = useState(null);
  const [driverLocations, setDriverLocations] = useState([]);

  const [filters, setFilters] = useState({
    date: new Date()?.toISOString()?.split('T')?.[0],
    driverId: 'all',
    zone: 'all'
  });

  const [formData, setFormData] = useState({
    date: new Date()?.toISOString()?.split('T')?.[0],
    timeWindow: '',
    clientName: '',
    clientPhone: '',
    clientAddress: '',
    clientCity: '',
    clientPostalCode: '',
    clientZone: '',
    shipmentId: '',
    containerId: '',
    driverId: '',
    status: 'planifie',
    notes: ''
  });

  const availableZones = React.useMemo(() => {
    const zonesSet = new Set();
    employees?.forEach(emp => {
      if (emp?.zone) zonesSet?.add(emp?.zone);
    });
    tasks?.forEach(task => {
      if (task?.clientZone) zonesSet?.add(task?.clientZone);
    });
    return Array.from(zonesSet)?.sort();
  }, [employees, tasks]);

  useEffect(() => {
    loadData();
  }, [filters?.date]);

  useEffect(() => {
    const loadDriverLocations = async () => {
      const { data, error: locError } = await supabase
        ?.from('driver_locations')
        ?.select('*');
      if (!locError) setDriverLocations(data || []);
    };

    loadDriverLocations();

    const channel = supabase
      ?.channel('driver-locations-shared')
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'driver_locations' },
        () => loadDriverLocations()
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [employeesData, shipmentsData, catalogData, containersData] = await Promise.all([
        employeeService?.getAll({ isActive: true }),
        shipmentService?.getShipments(),
        catalogService?.getAll(),
        containerService?.getOpenContainers()
      ]);
      
      const tasksData = await pickupTaskService?.getAll({ date: filters?.date });
      
      const enrichedTasks = tasksData?.map(task => {
        const linkedShipment = shipmentsData?.find(s => s?.id === task?.shipmentId);
        return {
          ...task,
          shipmentSenderCountry: linkedShipment?.senderCountry || null
        };
      });
      
      setTasks(enrichedTasks || []);
      setEmployees(employeesData || []);
      setShipments(shipmentsData || []);
      setContainers(containersData || []);
      setCatalogItems(catalogData || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      setError('');
      
      let notesWithItems = formData?.notes || '';
      if (selectedCatalogItems?.length > 0) {
        const itemsList = selectedCatalogItems
          ?.map(item => `${item?.quantity}x ${item?.name} (${item?.price}€)`)
          ?.join(', ');
        notesWithItems = notesWithItems ? `${notesWithItems}\n\nColis à collecter: ${itemsList}` : `Colis à collecter: ${itemsList}`;
      }
      
      const cityWithPostalCode = formData?.clientPostalCode 
        ? `${formData?.clientCity} ${formData?.clientPostalCode}` 
        : formData?.clientCity;
      
      const dataToSave = { 
        ...formData, 
        clientCity: cityWithPostalCode,
        notes: notesWithItems 
      };
      
      delete dataToSave?.clientPostalCode;
      
      if (editingTask) {
        await pickupTaskService?.update(editingTask?.id, dataToSave);
      } else {
        await pickupTaskService?.create(dataToSave);
      }
      
      await loadData();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving task:', err);
      setError(err?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    
    const postalCodeMatch = task?.clientCity?.match(/\b\d{5}\b/);
    const extractedPostalCode = postalCodeMatch?.[0] || '';
    const cityWithoutPostalCode = task?.clientCity?.replace(/\b\d{5}\b/g, '')?.trim();
    
    setFormData({
      date: task?.date,
      timeWindow: task?.timeWindow || '',
      clientName: task?.clientName,
      clientPhone: task?.clientPhone || '',
      clientAddress: task?.clientAddress || '',
      clientCity: cityWithoutPostalCode || task?.clientCity || '',
      clientPostalCode: extractedPostalCode,
      clientZone: task?.clientZone || '',
      shipmentId: task?.shipmentId || '',
      containerId: task?.containerId || '',
      driverId: task?.driverId || '',
      status: task?.status,
      notes: task?.notes || ''
    });
    setShowModal(true);
  };

  const handleAddCatalogItem = (item) => {
    setSelectedCatalogItems(prev => [...prev, { ...item, quantity: 1 }]);
  };

  const handleRemoveCatalogItem = (index) => {
    setSelectedCatalogItems(prev => prev?.filter((_, i) => i !== index));
  };

  const handleUpdateQuantity = (index, quantity) => {
    setSelectedCatalogItems(prev => 
      prev?.map((item, i) => i === index ? { ...item, quantity: parseInt(quantity) || 1 } : item)
    );
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTask(null);
    setSelectedCatalogItems([]);
    setFormData({
      date: new Date()?.toISOString()?.split('T')?.[0],
      timeWindow: '',
      clientName: '',
      clientPhone: '',
      clientAddress: '',
      clientCity: '',
      clientPostalCode: '',
      clientZone: '',
      shipmentId: '',
      containerId: '',
      driverId: '',
      status: 'planifie',
      notes: ''
    });
  };

  const handleRowClick = async (task) => {
    if (task?.status === 'termine') {
      setSelectedTask(task);
      setShowDetailsModal(true);
      await generateAllPhotoSignedUrls(task);
    }
  };

  const generateAllPhotoSignedUrls = async (task) => {
    try {
      setPhotoSignedUrls([]);
      setPhotoError('');
      
      const rawValue = task?.pickupPhotoUrls;
      let photoValues = [];
      
      if (Array.isArray(rawValue)) {
        photoValues = rawValue;
      } else if (typeof rawValue === 'string' && rawValue?.trim()?.startsWith('[')) {
        photoValues = JSON.parse(rawValue);
      } else if (typeof rawValue === 'string' && rawValue?.trim()) {
        photoValues = [rawValue];
      }
      
      if (photoValues?.length === 0) {
        setPhotoError('Aucune photo disponible');
        return;
      }
      
      const signedUrlPromises = photoValues?.map(async (photoValue) => {
        let filePath = '';
        
        if (photoValue?.startsWith('pickup-photos/')) {
          filePath = photoValue;
        } else if (photoValue?.includes('/storage/v1/object/public/shipment-photos/')) {
          const urlParts = photoValue?.split('/storage/v1/object/public/shipment-photos/');
          filePath = urlParts?.[1] || '';
        } else {
          console.error('❌ Unknown photo value format:', photoValue);
          return null;
        }
        
        const { data: signedData, error: signedError } = await supabase
          ?.storage
          ?.from('shipment-photos')
          ?.createSignedUrl(filePath, 3600);
        
        if (signedError || !signedData?.signedUrl) {
          console.error('❌ Failed to generate signed URL for:', filePath);
          return null;
        }
        
        return signedData?.signedUrl;
      });
      
      const signedUrls = await Promise.all(signedUrlPromises);
      const validUrls = signedUrls?.filter(url => url !== null);
      
      if (validUrls?.length === 0) {
        setPhotoError('Impossible de charger les photos');
        return;
      }
      
      setPhotoSignedUrls(validUrls);
    } catch (error) {
      console.error('❌ Error generating signed URLs:', error);
      setPhotoError(`Erreur: ${error?.message || 'Erreur inconnue'}`);
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedTask(null);
    setPhotoSignedUrls([]);
    setPhotoError('');
    setSelectedPhotoIndex(null);
  };

  const copySignatureLink = (taskId) => {
    const link = `${window.location?.origin}/pickup-signature/${taskId}`;
    navigator.clipboard?.writeText(link);
    alert('Lien copié! Vous pouvez l\'envoyer au chauffeur via WhatsApp.');
  };

  const filteredTasks = tasks?.filter(task => {
    const matchesDriver = filters?.driverId === 'all' || task?.driverId === filters?.driverId;
    const matchesZone = filters?.zone === 'all' || task?.clientZone === filters?.zone;
    return matchesDriver && matchesZone;
  });

  const selectedDriver = employees?.find(e => e?.id === filters?.driverId);
  const canShowRouteSheet = filters?.driverId !== 'all';
  
  const driverTasksForRouteSheet = canShowRouteSheet 
    ? filteredTasks?.filter(t => t?.driverId === filters?.driverId)?.sort((a, b) => {
        const timeA = a?.timeWindow || '';
        const timeB = b?.timeWindow || '';
        return timeA?.localeCompare(timeB);
      })
    : [];

  const openRouteSheet = () => {
    if (!canShowRouteSheet) {
      alert('Veuillez sélectionner un chauffeur précis pour générer sa feuille de route.');
      return;
    }
    setShowRouteSheet(true);
  };

   const statusColors = {
    'planifie': 'bg-blue-100 text-blue-800',
    'en_route': 'bg-indigo-100 text-indigo-800',
    'en_cours': 'bg-yellow-100 text-yellow-800',
    'collecte': 'bg-orange-100 text-orange-800',
    'depose': 'bg-purple-100 text-purple-800',
    'termine': 'bg-green-100 text-green-800',
    'annule': 'bg-red-100 text-red-800'
  };

  const statusLabels = {
    'planifie': 'Planifié',
    'en_route': 'En route',
    'en_cours': 'En cours',
    'collecte': 'Collecté',
    'depose': 'Déposé',
    'termine': 'Terminé',
    'annule': 'Annulé'
  };

  const generateShipmentReference = () => {
    const year = new Date()?.getFullYear();
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `ADT-${year}-${randomNum}`;
  };

  const handleEnvoiClick = async (task) => {
    try {
      setError('');
      
      const { data: fullTask, error: fetchError } = await supabase?.from('pickup_tasks')?.select('*')?.eq('id', task?.id)?.single();
      
      if (fetchError) throw fetchError;
      
      if (task?.shipmentId) {
        const { error: updateError } = await supabase?.from('shipments')?.update({
            photos_urls: fullTask?.pickup_photo_urls || [],
            description: fullTask?.notes || '',
            updated_at: new Date()?.toISOString()
          })?.eq('id', task?.shipmentId);
        
        if (updateError) throw updateError;
        
        navigate(`/shipment-detail-management/${task?.shipmentId}`);
        return;
      }

      const reference = generateShipmentReference();
      
      const { data: newShipment, error: createError } = await supabase?.from('shipments')?.insert({
          reference: reference,
          sender_name: task?.clientName,
          sender_phone: task?.clientPhone || '',
          sender_address: task?.clientAddress || '',
          sender_city: task?.clientCity || '',
          sender_country: 'France',
          receiver_name: 'À définir',
          receiver_phone: 'À définir',
          receiver_address: 'À définir',
          receiver_city: 'À définir',
          receiver_country: 'À définir',
          description: fullTask?.notes || '',
          photos_urls: fullTask?.pickup_photo_urls || [],
          current_status: 'pending',
          shipment_type: 'colis'
        })?.select()?.single();

      if (createError) throw createError;

      await pickupTaskService?.update(task?.id, {
        shipmentId: newShipment?.id
      });

      navigate(`/shipment-detail-management/${newShipment?.id}`);
    } catch (err) {
      console.error('Error creating shipment from pickup:', err);
      setError(err?.message || 'Erreur lors de la création de l\'envoi');
    }
  };

  const handleAssignToShipment = async (task) => {
    try {
      setError('');
      
      const { data: fullTask, error: fetchError } = await supabase?.from('pickup_tasks')?.select('*')?.eq('id', task?.id)?.single();
      
      if (fetchError) throw fetchError;
      
      const reference = generateShipmentReference();
      
      const { data: newShipment, error: createError } = await supabase?.from('shipments')?.insert({
          reference: reference,
          sender_name: task?.clientName,
          sender_phone: task?.clientPhone || '',
          sender_address: task?.clientAddress || '',
          sender_city: task?.clientCity || '',
          sender_country: 'France',
          receiver_name: 'À définir',
          receiver_phone: 'À définir',
          receiver_address: 'À définir',
          receiver_city: 'À définir',
          receiver_country: 'À définir',
          description: fullTask?.notes || '',
          photos_urls: fullTask?.pickup_photo_urls || [],
          current_status: 'pending',
          shipment_type: 'colis'
        })?.select()?.single();

      if (createError) throw createError;

      await pickupTaskService?.update(task?.id, {
        shipmentId: newShipment?.id
      });

      await loadData();
      
      alert(`Envoi ${reference} créé avec succès pour ${task?.clientName}`);
    } catch (err) {
      console.error('Error creating shipment from pickup:', err);
      setError(err?.message || 'Erreur lors de la création de l\'envoi');
    }
  };

  const handleAssignToContainer = (task) => {
    if (!task?.shipmentId) {
      alert('Ce client doit d\'abord avoir un envoi créé avant d\'être assigné à un conteneur.');
      return;
    }
    setTaskToAssign(task);
    setShowAssignContainerModal(true);
  };

  const handleContainerAssigned = async () => {
    await loadData();
    alert('Envoi assigné au conteneur avec succès!');
  };

  const franceDrivers = useMemo(() => {
    return employees?.filter(emp => {
      const zone = emp?.zone?.toLowerCase() || '';
      return !zone?.includes('abidjan') && !zone?.includes('côte d\'ivoire') && !zone?.includes('cote d\'ivoire');
    });
  }, [employees]);

  useEffect(() => {
    const subscription = realtimeService?.subscribeToPickupTasks((payload) => {
      if (payload?.eventType === 'INSERT') {
        setTasks(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setTasks(prev => prev?.map(task =>
          task?.id === payload?.new?.id ? payload?.new : task
        ));
      } else if (payload?.eventType === 'DELETE') {
        setTasks(prev => prev?.filter(task => task?.id !== payload?.old?.id));
      }
    });

    return () => {
      realtimeService?.unsubscribe('pickup-tasks-realtime');
    };
  }, []);

  const generateFileName = (type, index = null) => {
    const timestamp = new Date()?.toISOString()?.split('T')?.[0];
    const clientName = selectedTask?.clientName?.replace(/\s+/g, '_') || 'client';
    if (type === 'signature') {
      return `signature_${clientName}_${timestamp}.png`;
    } else if (type === 'photo') {
      return `photo_${index}_${clientName}_${timestamp}.jpg`;
    }
    return `file_${timestamp}`;
  };

  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response?.blob();
      const blobUrl = window.URL?.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      window.URL?.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Erreur lors du téléchargement du fichier');
    }
  };

  const downloadAllAsZip = async () => {
    try {
      alert('Téléchargement des fichiers... Veuillez patienter.');
      
      if (selectedTask?.clientSignatureUrl) {
        await downloadFile(selectedTask?.clientSignatureUrl, generateFileName('signature'));
      }
      
      for (let i = 0; i < photoSignedUrls?.length; i++) {
        await downloadFile(photoSignedUrls?.[i], generateFileName('photo', i + 1));
      }
      
      alert('Tous les fichiers ont été téléchargés avec succès!');
    } catch (error) {
      console.error('Error downloading all files:', error);
      alert('Erreur lors du téléchargement des fichiers');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement du planning...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Planning des Collectes</h1>
          <p className="mt-2 text-sm text-gray-600">
            Planifiez et gérez les collectes par chauffeur et par zone
          </p>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Filters + Route Sheet Button */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={filters?.date}
                onChange={(e) => setFilters({ ...filters, date: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chauffeur</label>
              <select
                value={filters?.driverId}
                onChange={(e) => setFilters({ ...filters, driverId: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">Tous les chauffeurs</option>
                {employees?.map(emp => (
                  <option key={emp?.id} value={emp?.id}>{emp?.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zone</label>
              <select
                value={filters?.zone}
                onChange={(e) => setFilters({ ...filters, zone: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">Toutes les zones</option>
                {availableZones?.map(zone => (
                  <option key={zone} value={zone}>{zone}</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={openRouteSheet}
                disabled={!canShowRouteSheet}
                className={`w-full px-4 py-2 rounded-lg transition-colors font-medium ${
                  canShowRouteSheet 
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700' :'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title={!canShowRouteSheet ? 'Sélectionnez un chauffeur pour générer sa feuille de route' : ''}
              >
                📋 Feuille de route
              </button>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setShowModal(true)}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                + Créer une Collecte
              </button>
            </div>
          </div>
        </div>

        {/* Carte des positions chauffeurs */}
        <DriverLocationsMap locations={driverLocations} />

        {/* Tasks Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Heure</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adresse</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chauffeur</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ETA</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTasks?.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    Aucune collecte planifiée pour cette date
                  </td>
                </tr>
              ) : (
                filteredTasks?.map((task) => (
                  <tr 
                    key={task?.id} 
                    onClick={() => handleRowClick(task)}
                    className={`hover:bg-gray-50 ${task?.shipmentId ? 'cursor-pointer' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {task?.timeWindow || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{task?.clientName}</div>
                      {task?.clientPhone && (
                        <div className="text-sm text-gray-500">{task?.clientPhone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{task?.clientAddress || '-'}</div>
                      {task?.clientCity && (
                        <div className="text-sm text-gray-500">{task?.clientCity}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {task?.clientZone || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {task?.driver?.name || 'Non assigné'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <TaskETA task={task} driverLocations={driverLocations} employees={employees} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors?.[task?.status]}`}>
                        {statusLabels?.[task?.status]}
                      </span>
                      {task?.clientSignatureUrl && (
                        <span className="ml-2 text-green-600" title="Signature client reçue">✓</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2" onClick={(e) => e?.stopPropagation()}>
                      {task?.status !== 'termine' && (
                        <button
                          onClick={() => handleEdit(task)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Modifier
                        </button>
                      )}
                      {task?.status === 'termine' && (
                        <button
                          onClick={() => handleEnvoiClick(task)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Envoi
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Collected Clients Table */}
        <div className="mt-8">
          <CollectedClientsTable
            tasks={tasks}
            onAssignToShipment={handleAssignToShipment}
            onAssignToContainer={handleAssignToContainer}
            loading={loading}
          />
        </div>
      </div>

      {/* Create/Edit Task Modal (FIX: z-index remonté au-dessus de la carte) */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {editingTask ? 'Modifier Collecte' : 'Nouvelle Collecte'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    value={formData?.date}
                    onChange={(e) => setFormData({ ...formData, date: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Créneau Horaire</label>
                  <input
                    type="text"
                    value={formData?.timeWindow}
                    onChange={(e) => setFormData({ ...formData, timeWindow: e?.target?.value })}
                    placeholder="Ex: 08h-10h"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom Client *</label>
                  <input
                    type="text"
                    required
                    value={formData?.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone Client</label>
                  <input
                    type="tel"
                    value={formData?.clientPhone}
                    onChange={(e) => setFormData({ ...formData, clientPhone: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adresse Client</label>
                  <input
                    type="text"
                    value={formData?.clientAddress}
                    onChange={(e) => setFormData({ ...formData, clientAddress: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                  <input
                    type="text"
                    value={formData?.clientCity}
                    onChange={(e) => setFormData({ ...formData, clientCity: e?.target?.value })}
                    placeholder="Ex: Paris"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code Postal</label>
                  <input
                    type="text"
                    value={formData?.clientPostalCode}
                    onChange={(e) => setFormData({ ...formData, clientPostalCode: e?.target?.value })}
                    placeholder="Ex: 75015"
                    maxLength="5"
                    pattern="[0-9]{5}"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zone</label>
                  <input
                    type="text"
                    value={formData?.clientZone}
                    onChange={(e) => setFormData({ ...formData, clientZone: e?.target?.value })}
                    placeholder="Ex: Nord, Sud, Evry"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lier à un Envoi</label>
                  <select
                    value={formData?.shipmentId}
                    onChange={(e) => setFormData({ ...formData, shipmentId: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Aucun envoi</option>
                    {shipments?.slice(0, 50)?.map(shipment => (
                      <option key={shipment?.id} value={shipment?.id}>
                        {shipment?.reference} - {shipment?.senderName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lier à un Conteneur</label>
                  <select
                    value={formData?.containerId}
                    onChange={(e) => setFormData({ ...formData, containerId: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Aucun conteneur</option>
                    {containers?.map(container => (
                      <option key={container?.id} value={container?.id}>
                        {container?.containerCode} — {container?.departurePort} → {container?.destinationPort}
                        {container?.departureDate ? ` (départ: ${new Date(container?.departureDate)?.toLocaleDateString('fr-FR')})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chauffeur</label>
                  <select
                    value={formData?.driverId}
                    onChange={(e) => setFormData({ ...formData, driverId: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Non assigné</option>
                    {franceDrivers?.map(emp => (
                      <option key={emp?.id} value={emp?.id}>{emp?.name} - {emp?.zone}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select
                    value={formData?.status}
                    onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="planifie">Planifié</option>
                    <option value="en_cours">En cours</option>
                    <option value="termine">Terminé</option>
                    <option value="annule">Annulé</option>
                  </select>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Colis à collecter</h3>
                
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ajouter un article du catalogue</label>
                  <select
                    onChange={(e) => {
                      const item = catalogItems?.find(i => i?.id === e?.target?.value);
                      if (item) {
                        handleAddCatalogItem(item);
                        e.target.value = '';
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner un article...</option>
                    {catalogItems?.map(item => (
                      <option key={item?.id} value={item?.id}>
                        {item?.name} - {item?.price}€ ({item?.category})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCatalogItems?.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {selectedCatalogItems?.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item?.name}</p>
                          <p className="text-sm text-gray-600">{item?.price}€ - {item?.category}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            value={item?.quantity}
                            onChange={(e) => handleUpdateQuantity(index, e?.target?.value)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveCatalogItem(index)}
                            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="text-right font-semibold text-gray-900">
                      Total attendu: {selectedCatalogItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0)}€
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData?.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  {editingTask ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign to Container Modal */}
      {showAssignContainerModal && taskToAssign && (
        <AssignToContainerModal
          task={taskToAssign}
          onClose={() => {
            setShowAssignContainerModal(false);
            setTaskToAssign(null);
          }}
          onAssign={handleContainerAssigned}
        />
      )}

      {/* Route Sheet Modal (FIX: z-index remonté au-dessus de la carte) */}
      {showRouteSheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                📋 Feuille de Route - {selectedDriver?.name}
              </h2>
              <button
                onClick={() => setShowRouteSheet(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Chauffeur</p>
                  <p className="font-medium">{selectedDriver?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Date</p>
                  <p className="font-medium">{filters?.date}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Zone</p>
                  <p className="font-medium">{selectedDriver?.zone || 'Non définie'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Nombre de collectes</p>
                  <p className="font-medium">{driverTasksForRouteSheet?.length}</p>
                </div>
              </div>
            </div>

            {hasAnyFrancePickups(driverTasksForRouteSheet) && (
              <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  🗺️ Navigation GPS de toute la tournée (France uniquement)
                </h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  
                  <a
                    href={generateCompleteRouteUrl(driverTasksForRouteSheet, false)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
                  >
                    <span className="mr-2">📍</span>
                    Ouvrir toute la tournée
                  </a>
                  
                  <a
                    href={generateCompleteRouteUrl(driverTasksForRouteSheet, true)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition"
                  >
                    <span className="mr-2">🚀</span>
                    Optimiser l'ordre
                  </a>
                </div>
                <p className="mt-2 text-xs text-gray-600">
                  • "Ouvrir toute la tournée" : navigation avec les arrêts dans l'ordre actuel de la feuille de route
                  <br />
                  • "Optimiser l'ordre" : Google Maps réorganise automatiquement les arrêts pour le trajet le plus efficace
                </p>
              </div>
            )}

            <div className="space-y-4">
              {driverTasksForRouteSheet?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Aucune collecte planifiée pour ce chauffeur à cette date
                </div>
              ) : (
                driverTasksForRouteSheet?.map((task, index) => (
                  <div key={task?.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                            <div>
                              <p className="text-xs text-gray-500">Heure / Créneau</p>
                              <p className="font-medium">{task?.timeWindow || 'Non défini'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Client</p>
                              <p className="font-medium">{task?.clientName}</p>
                              {task?.clientPhone && (
                                <p className="text-sm text-gray-600">{task?.clientPhone}</p>
                              )}
                            </div>
                            <div className="col-span-2">
                              <p className="text-xs text-gray-500">Adresse</p>
                              <p className="text-sm">
                                {task?.clientAddress || '-'}
                                {task?.clientCity && `, ${task?.clientCity}`}
                              </p>
                              
                              <NavigationButtons task={task} />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Zone</p>
                              <p className="text-sm">{task?.clientZone || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Statut</p>
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors?.[task?.status]}`}>
                                {statusLabels?.[task?.status]}
                              </span>
                            </div>
                            {task?.notes && (
                              <div className="col-span-2">
                                <p className="text-xs text-gray-500">Notes</p>
                                <p className="text-sm text-gray-700">{task?.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowRouteSheet(false)}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal (FIX: z-index remonté au-dessus de la carte) */}
      {showDetailsModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-3xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Détails de la collecte
              </h2>
              <button
                onClick={handleCloseDetailsModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Informations Client</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Client</p>
                    <p className="font-medium">{selectedTask?.clientName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Téléphone</p>
                    <p className="font-medium">{selectedTask?.clientPhone || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600">Adresse</p>
                    <p className="font-medium">
                      {selectedTask?.clientAddress}
                      {selectedTask?.clientCity && `, ${selectedTask?.clientCity}`}
                    </p>
                    
                    <NavigationButtons task={selectedTask} />
                  </div>
                </div>
              </div>

              {selectedTask?.notes && (
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Notes</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedTask?.notes}</p>
                  </div>
                </div>
              )}

              {(selectedTask?.clientSignatureUrl || photoSignedUrls?.length > 0) && (
                <div className="border-2 border-green-500 rounded-lg p-4 bg-green-50">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileDown className="w-5 h-5 text-green-600" />
                      Télécharger les preuves
                    </h3>
                  </div>
                  
                  <div className="space-y-2">
                    <button
                      onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <Download className="w-5 h-5" />
                      Télécharger séparément
                    </button>

                    {showDownloadMenu && (
                      <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                        {selectedTask?.clientSignatureUrl && (
                          <button
                            onClick={() => downloadFile(selectedTask?.clientSignatureUrl, generateFileName('signature'))}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded flex items-center gap-2 text-sm"
                          >
                            <Download className="w-4 h-4" />
                            Signature client ({generateFileName('signature')})
                          </button>
                        )}
                        
                        {photoSignedUrls?.map((url, index) => (
                          <button
                            key={index}
                            onClick={() => downloadFile(url, generateFileName('photo', index + 1))}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded flex items-center gap-2 text-sm"
                          >
                            <Download className="w-4 h-4" />
                            Photo {index + 1} ({generateFileName('photo', index + 1)})
                          </button>
                        ))}
                      </div>
                    )}

                    <button
                      onClick={downloadAllAsZip}
                      className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <FileDown className="w-5 h-5" />
                      Tout télécharger (ZIP)
                    </button>
                    
                    <p className="text-xs text-gray-600 text-center">
                      {selectedTask?.clientSignatureUrl ? '1 signature' : '0 signature'} + {photoSignedUrls?.length} photo(s)
                    </p>
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Signature Client</h3>
                {selectedTask?.clientSignatureUrl ? (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <img
                      src={selectedTask?.clientSignatureUrl}
                      alt="Signature du client"
                      className="w-full h-auto max-h-64 object-contain"
                    />
                  </div>
                ) : (
                  <div className="border rounded-lg p-8 bg-gray-50 text-center text-gray-500">
                    <p>Aucune signature disponible</p>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Photos du Colis ({photoSignedUrls?.length})
                </h3>
                
                {photoSignedUrls?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {photoSignedUrls?.map((url, index) => (
                      <div 
                        key={index} 
                        className="relative border-2 border-blue-200 rounded-lg overflow-hidden cursor-pointer hover:border-blue-400 transition group"
                        onClick={() => setSelectedPhotoIndex(index)}
                      >
                        <img 
                          src={url} 
                          alt={`Photo ${index + 1}`}
                          className="w-full h-40 object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition flex items-center justify-center">
                          <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition" />
                        </div>
                        <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                          Photo {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : photoError ? (
                  <div className="border rounded-lg p-8 bg-red-50 text-center">
                    <p className="text-red-600 font-medium">{photoError}</p>
                    <button
                      onClick={() => generateAllPhotoSignedUrls(selectedTask)}
                      className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                    >
                      Réessayer
                    </button>
                  </div>
                ) : (
                  <div className="border rounded-lg p-8 bg-gray-50 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-2"></div>
                    <p className="text-gray-500">Chargement des photos...</p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleCloseDetailsModal}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal for Photo Viewing (FIX: z-index remonté au-dessus de la carte) */}
      {selectedPhotoIndex !== null && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[10000] flex items-center justify-center p-4"
          onClick={() => setSelectedPhotoIndex(null)}
        >
          <div className="relative max-w-6xl w-full">
            <button
              onClick={() => setSelectedPhotoIndex(null)}
              className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-gray-200 transition z-10"
            >
              <X className="w-6 h-6" />
            </button>
            
            <img 
              src={photoSignedUrls?.[selectedPhotoIndex]} 
              alt={`Photo ${selectedPhotoIndex + 1}`}
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e?.stopPropagation()}
            />
            
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-full text-sm font-medium">
              Photo {selectedPhotoIndex + 1} / {photoSignedUrls?.length}
            </div>
            
            {photoSignedUrls?.length > 1 && (
              <>
                {selectedPhotoIndex > 0 && (
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      setSelectedPhotoIndex(selectedPhotoIndex - 1);
                    }}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-3 hover:bg-gray-200 transition"
                  >
                    ←
                  </button>
                )}
                {selectedPhotoIndex < photoSignedUrls?.length - 1 && (
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      setSelectedPhotoIndex(selectedPhotoIndex + 1);
                    }}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-3 hover:bg-gray-200 transition"
                  >
                    →
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PickupTaskPlanningSystem;